import React from "react";

// Assets
import pgaLogo from "@/assets/images/pga-logo.svg";
import pgaLogoDecember from "@/assets/images/pga-logo-december.png";
import pgaLogoAprilFools from "@/assets/images/pga-logo-april-fools.gif";

export interface PageMessageProps {
  /**
   * Main heading text
   */
  title: string;
  /**
   * Descriptive message text
   */
  message?: string;
  /**
   * Whether to show the animated loading dots
   */
  showLoadingIndicator?: boolean;
  /**
   * Whether to show the PGA logo
   */
  showLogo?: boolean;
  /**
   * Child component to render inside the message container
   */
  children?: React.ReactNode;
}

const PageMessage: React.FC<PageMessageProps> = ({
  title,
  message,
  showLoadingIndicator = true,
  showLogo = true,
  children
}) => {
  const today = new Date();
  const isDecember = today.getMonth() === 11;
  const isAprilFoolsDay = today.getMonth() === 3 && today.getDate() === 1;

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-sys-brand-surface">
      <div className="relative z-10 flex flex-col items-center justify-center text-center px-6 max-w-md">
        {/* PGA Logo */}
        {showLogo && (
          <div className="mb-8">
            {isAprilFoolsDay ? (
              <img src={pgaLogoAprilFools} alt="PGA of America" className="h-32 w-auto mx-auto" />
            ) : isDecember ? (
              <img src={pgaLogoDecember} alt="PGA of America" className="w-32 h-32 mx-auto" />
            ) : (
              <img src={pgaLogo} alt="PGA of America" className="w-32 h-32 mx-auto" />
            )}
          </div>
        )}

        {/* Content */}
        <div className="space-y-4">
          <h2 className="text-title-large-bold font-serif text-extended-pgadarknavy-brand-color">{title}</h2>
          {message && (
            <p className="text-extended-pgadarknavy-brand-color opacity-80 text-title-small leading-relaxed font-semibold">
              {message}
            </p>
          )}

          {/* Loading indicator */}
          {showLoadingIndicator && (
            <div className="flex justify-center pt-4">
              <div className="flex space-x-1">
                <div className="w-2 h-2 bg-extended-pgadarknavy-brand-color-bright rounded-full animate-bounce"></div>
                <div
                  className="w-2 h-2 bg-extended-pgadarknavy-brand-color-bright rounded-full animate-bounce"
                  style={{ animationDelay: "0.1s" }}
                ></div>
                <div
                  className="w-2 h-2 bg-extended-pgadarknavy-brand-color-bright rounded-full animate-bounce"
                  style={{ animationDelay: "0.2s" }}
                ></div>
              </div>
            </div>
          )}

          {children}
        </div>
      </div>
    </div>
  );
};

export default PageMessage;
